import type { ModuleConfig } from '../types/module'

/**
 * Validate that all module dependencies are met.
 *
 * @throws Error if a module depends on a disabled module
 */
export function validateDependencies(
  enabledModules: string[],
  moduleConfigs: Map<string, ModuleConfig>
): void {
  const enabledSet = new Set(enabledModules)

  for (const moduleName of enabledModules) {
    const config = moduleConfigs.get(moduleName)
    if (!config) {
      throw new Error(`Module "${moduleName}" is enabled but not registered`)
    }

    for (const dep of config.dependencies) {
      if (!enabledSet.has(dep)) {
        throw new Error(
          `Module "${moduleName}" depends on "${dep}", but "${dep}" is not enabled`
        )
      }
    }
  }
}

/**
 * Topologically sort modules based on their dependencies.
 * Modules with no dependencies come first.
 *
 * @throws Error on circular dependencies
 */
export function topologicalSort(
  enabledModules: string[],
  moduleConfigs: Map<string, ModuleConfig>
): string[] {
  const sorted: string[] = []
  const visited = new Set<string>()
  const visiting = new Set<string>() // for cycle detection

  function visit(name: string): void {
    if (visited.has(name)) return
    if (visiting.has(name)) {
      throw new Error(`Circular dependency detected involving module "${name}"`)
    }

    visiting.add(name)

    const config = moduleConfigs.get(name)
    if (config) {
      for (const dep of config.dependencies) {
        if (enabledModules.includes(dep)) {
          visit(dep)
        }
      }
    }

    visiting.delete(name)
    visited.add(name)
    sorted.push(name)
  }

  for (const name of enabledModules) {
    visit(name)
  }

  return sorted
}

/**
 * Module registry - stores all registered modules
 */
export class ModuleRegistry {
  private modules = new Map<string, ModuleConfig>()

  /**
   * Register a module configuration
   */
  register(config: ModuleConfig): void {
    if (this.modules.has(config.name)) {
      throw new Error(`Module "${config.name}" is already registered`)
    }
    this.modules.set(config.name, config)
  }

  /**
   * Get a registered module by name
   */
  get(name: string): ModuleConfig | undefined {
    return this.modules.get(name)
  }

  /**
   * Get all registered modules
   */
  getAll(): Map<string, ModuleConfig> {
    return new Map(this.modules)
  }

  /**
   * Load enabled modules: validate dependencies, sort, and return in order
   *
   * @param enabledModules - Record of module name to enabled flag (from kubdee.config.ts)
   * @returns Sorted array of module configs
   */
  loadEnabled(enabledModules: Record<string, boolean>): ModuleConfig[] {
    const enabledNames = Object.entries(enabledModules)
      .filter(([, enabled]) => enabled)
      .map(([name]) => name)

    // Validate all enabled modules are registered
    for (const name of enabledNames) {
      if (!this.modules.has(name)) {
        throw new Error(
          `Module "${name}" is enabled in config but not registered. ` +
            `Make sure to import and register it first.`
        )
      }
    }

    // Validate dependencies
    validateDependencies(enabledNames, this.modules)

    // Topological sort
    const sorted = topologicalSort(enabledNames, this.modules)

    // Return configs in sorted order
    return sorted
      .map((name) => this.modules.get(name))
      .filter((config): config is ModuleConfig => config !== undefined)
  }

  /**
   * Clear all registered modules (useful for testing)
   */
  clear(): void {
    this.modules.clear()
  }
}

/**
 * Global module registry singleton
 */
export const moduleRegistry = new ModuleRegistry()
